import React, {ReactNode, useContext} from 'react';
import {OnChangeValue, Props as SelectProps } from 'react-select';
import Select from 'react-select';
import AsyncSelect from 'react-select';
import { Props as AsyncProps } from 'react-select';
import { ActionMeta } from 'react-select';
import {LanguageProp} from "../../config/LanguageConfig";
import {Denseable} from "../../models/common/ComponentBehavioural";
import {InputLockedValuesContext, LanguageContext, ViewIdentifierContext} from "../../config/ContextConfig";
import {useElementFocus} from "../../config/hooks/useElementFocus";
import {VisibilityHandler} from "../common/VisibilityHandler";
import {TextDisplay} from "../common/data-display/TextDisplay";
import {useDispatch} from "react-redux";
import {ActiveDataFilterItem} from "../common/data-filters/ActiveDataFilterItem";
import { removeLockedFormInputValue} from "../../actions/common/FormActions";
import {FilterTypes} from "../../helpers/FilterTypes";


/** Represents a single option within the searchable select dropdown. */
export interface OptionType {
    /** The human-readable text displayed for this option. */
    label: string 
    /** The underlying value submitted when this option is selected. */
    value: string
}

/** Union type representing a single option, a readonly array, or a mutable array of options. */
export type SelectOptionTypes< T extends OptionType = OptionType > = T | readonly T[] | T[]

/** Props specific to the {@link SearchableSelect} component, excluding the underlying react-select props. */
export interface SearchableSelectOwnProps<T extends OptionType = OptionType> extends Partial<LanguageProp>, Denseable {
    /** The label displayed above the select field inside the fieldset legend. */
    label: string
    /** Marks the field as required and appends an asterisk to the label. */
    required?: boolean
    /** Additional CSS class name applied to the inner react-select component. */
    selectClassName?: string
    /**
     * Callback fired when the selected value changes.
     * @param value - The newly selected option(s).
     * @param action - Metadata describing the change action (e.g. select, clear).
     */
    onSelectChange?: ( value: SelectOptionTypes<T>, action: ActionMeta<T> ) => void
    /** When true, renders an AsyncSelect variant instead of the standard Select. */
    isAsync?: boolean
    /** Optional helper text displayed below the select field. */
    helperText?: ReactNode
    /** Error state configuration for inline validation feedback. */
    error?: { hasError: boolean, message: ReactNode }
    //variant?: "standard" | "button"
}

/**
 * Combined props type that merges {@link SearchableSelectOwnProps} with either
 * the standard react-select `SelectProps` or the async `AsyncProps`.
 */
export type SearchableSelectProps<T extends OptionType = OptionType> = SearchableSelectOwnProps<T> & SelectProps< T > | SearchableSelectOwnProps<T> & AsyncProps< T >



/**
 * A searchable dropdown select component that wraps react-select with
 * fieldset styling, locked-value support, validation error display, and
 * an optional dense (compact) rendering mode.
 *
 * When a value is locked via {@link InputLockedValuesContext}, the select
 * is replaced by an {@link ActiveDataFilterItem} chip that the user can dismiss.
 *
 * Supports both synchronous and asynchronous option loading via the `isAsync` prop.
 */
function SearchableSelect<T extends OptionType = OptionType >( props: SearchableSelectProps< T >) {
    const [ isFocused, { focusEventHandler, blurEventHandler } ] = useElementFocus( false );
    const language = useContext( LanguageContext );
    const viewIdentifier = useContext( ViewIdentifierContext );
    const inputLockedValues = useContext( InputLockedValuesContext );
    const dispatch = useDispatch();
    const isRequired = props.required || false;


    /** Delegates value changes to the consumer's onSelectChange callback, guarding against null/undefined values. */
    const onChange = ( value: OnChangeValue< T, any >, action: ActionMeta< T > ) => {

        if( typeof props.onSelectChange !== 'undefined' &&  typeof value !== "undefined" && value !== null ) {

            props.onSelectChange( value, action );
        }
    };

    /** Determines whether the current input has a locked value from context. */
    let isValueLocked = false;

    if( inputLockedValues.values[ props.name ?? "" ]  ) {
        isValueLocked = true;
    }

    /**
     * Wraps the select component with the appropriate layout.
     * In dense mode, renders children without additional markup.
     * Otherwise, wraps them in a styled fieldset with legend, locked-value chip,
     * helper text, and error message sections.
     */
    const selectWrapper = ( children: ReactNode | ReactNode[] ) => {
        if( props.dense ) {
            return (
                <>
                    { children }
                </>
            );
        } else {
           return (
               <div style={{ textAlign: "left"}}>
                   <fieldset
                       className={" text-left input-select-fieldset " + ( ( props.error?.hasError ) ? "input-select-fieldset-error " : "" ) + (( isFocused ) ? " input-select-fieldset--is-focused " + (props.className || "") : "" + (props.className || ""))}>
                       <legend>{ props.label }{ isRequired ? ' *' : ''}</legend>
                       {
                           isValueLocked
                           ? (
                                   <ActiveDataFilterItem
                                       activeFilter={{
                                           isInverted: false,
                                           identifier: props.name || "",
                                           type: FilterTypes.ID,
                                           selectedValue: inputLockedValues.values[ props.name || "" ].value,
                                           displayName: inputLockedValues.values[ props.name || "" ].label
                                       }}
                                       onRemoveFilter={() => {
                                           if( props.name ) {
                                               const values = inputLockedValues.values;
                                               delete values[ props.name || "" ];
                                               dispatch( removeLockedFormInputValue( viewIdentifier, props.name ) );
                                               inputLockedValues.onRemoveInputLock( props.name );
                                           }

                                       }}
                                       viewIdentifier={ viewIdentifier }
                                   />
                               )
                           : null
                       }
                       <VisibilityHandler isVisible={ ! isValueLocked }>
                           { children }
                       </VisibilityHandler>
                   </fieldset>
                   <VisibilityHandler isVisible={ typeof props.helperText !== "undefined" || props.error?.hasError === true }>
                       <div style={{ paddingLeft: "10px" }}>
                           {
                               typeof props.helperText === "string" &&  ! props.error?.hasError
                               ? (
                                       <div style={{ paddingLeft: "10px" }}>
                                           <TextDisplay variant="body2">
                                               { props.helperText }
                                           </TextDisplay>

                                       </div>
                                   )
                               : (
                                   <div style={{ paddingLeft: "10px" }}>
                                       { props.helperText }
                                   </div>
                                   )
                           }

                           {
                               props.error?.hasError
                                   ? typeof props.error?.message === "string"
                                       ? (
                                           <div style={{ paddingLeft: "10px" }}>
                                               <TextDisplay variant="body2">
                                                   { props.error.message }
                                               </TextDisplay>

                                           </div>
                                       )
                                       : (
                                           <div style={{ paddingLeft: "10px" }}>
                                               { props.error.message }
                                           </div>
                                       )
                                   : null
                           }

                       </div>
                   </VisibilityHandler>


               </div>
           );
        }
    };

    /** Resolves the CSS class for the select based on dense mode. */
    let selectStyleClass = props.dense
        ? " input-select-dense "
        : " input-select "

  /*  if( props.variant === "button" ) {
        selectStyleClass = selectStyleClass + " variant-button";
    }
*/
    /** The rendered select component — either standard or async depending on props. */
    var selectComponent = (
        <Select
            {...props}
            onBlur={blurEventHandler}
            name={ props.name }
            onFocus={focusEventHandler}
            className={ selectStyleClass + (props.selectClassName || "")}
            classNamePrefix="searchable"
            onChange={onChange}
        />
    );

    if( props.isAsync ) {

        selectComponent = (
                <AsyncSelect
                    {...props}
                    name={ props.name }
                    onBlur={ blurEventHandler }
                    onFocus={ focusEventHandler }
                    className={ selectStyleClass + (props.selectClassName || "")}
                    classNamePrefix="searchable"
                    onChange={onChange}
                    placeholder={(props.placeholder) ? props.placeholder : language.common.selectPlaceholder}
                />
        );

    }

    return selectWrapper( selectComponent );
}

export default SearchableSelect;
